import { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Navigate } from 'react-router-dom';
import { Alert, Card, CircularProgress, Container, Skeleton, Stack, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import Iconify from '../components/iconify';
import axios from '../utils/Axios';
import useEventListener from '../hooks/useEventListener';
import { useAuthContext } from '../auth/useAuthContext';
import { accountZone } from '../sections/@dashboard/holidays';
import {
  CLAIM_SUCCESS,
  ConnectionCard,
  DISCONNECT_ONLY_NOTE,
  DisconnectDialog,
  INTRO,
  NOTHING_TICKED,
  errorText,
  isCalendarOff,
  pausedNote,
  returnErrorText,
} from '../sections/@dashboard/calendar';
import DestinationCard from '../sections/@dashboard/calendar/DestinationCard';
import DestinationPrompt from '../sections/@dashboard/calendar/DestinationPrompt';

// After the last connection of an account outside the rollout is removed, nothing is left
// to show but the heading and the notice (spec 9.4).
function isEmptyView(view) {
  return view.mode === 'disconnect_only' && !(view.connections || []).length;
}

// The oldest "last checked" time of the connections, for the paused note.
function oldestCheckedAt(connections) {
  const times = connections.map((connection) => connection.lastCheckedAt).filter(Boolean);
  if (!times.length) return null;
  return times.reduce((oldest, time) => (new Date(time) < new Date(oldest) ? time : oldest));
}

// The first script in public/index.html moves `calendar_claim` from the fragment to
// sessionStorage before any analytics script starts (issue #55). The key is removed at
// once, so the code is posted at most once. Blocked storage reads as no stored claim.
function takeStoredClaim() {
  try {
    const claim = window.sessionStorage.getItem('fb_calendar_claim');
    window.sessionStorage.removeItem('fb_calendar_claim');
    return claim;
  } catch (error) {
    return null;
  }
}

// The Google Calendar page (design spec section 9). Every answer of the calendar routes
// is the whole view of spec 2.1, so the page replaces its view with each answer.
export default function CalendarIntegrationsPage() {
  const { user } = useAuthContext();
  const zone = accountZone(user);

  const [view, setView] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isOff, setIsOff] = useState(false);
  const [isClaiming, setIsClaiming] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [toDisconnect, setToDisconnect] = useState(null);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [notice, setNotice] = useState(null);
  const [prompt, setPrompt] = useState(null);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [isSavingDestination, setIsSavingDestination] = useState(false);
  const noticeTimer = useRef(null);
  // Goes up when a write starts. A view read that started before a write is older than
  // the write's own answer, so it is dropped.
  const writeCount = useRef(0);

  // A success notice clears after 6 seconds, unless `keep` is set. An error stays until the
  // owner closes it.
  const showNotice = useCallback((message, severity = 'success', keep = false) => {
    clearTimeout(noticeTimer.current);
    setNotice({ message, severity });
    if (severity === 'success' && !keep) noticeTimer.current = setTimeout(() => setNotice(null), 6000);
  }, []);

  useEffect(() => () => clearTimeout(noticeTimer.current), []);

  // `calendar_off` from `GET /calendar` means the rollout does not include this account and
  // it has no connection. The page then shows the 404 page, as for an unknown page.
  const loadView = useCallback(async () => {
    const writesBefore = writeCount.current;
    try {
      const response = await axios.get('/calendar');
      if (writeCount.current === writesBefore) setView(response.data);
    } catch (error) {
      if (isCalendarOff(error)) setIsOff(true);
      else showNotice(errorText(error), 'error');
    } finally {
      setIsLoading(false);
    }
  }, [showNotice]);

  // The other routes also answer `calendar_off` to an account with connections when the
  // rollout no longer includes it. The view then decides between the 404 page and the
  // disconnect-only view (spec 2).
  const showRefusal = useCallback(
    (error) => {
      if (isCalendarOff(error)) loadView();
      else showNotice(errorText(error), 'error');
    },
    [loadView, showNotice]
  );

  const claimConnection = useCallback(
    async (claim) => {
      writeCount.current += 1;
      setIsClaiming(true);
      try {
        const response = await axios.post('/calendar/google/claim', { claim });
        setView(response.data);
        setIsLoading(false);
        showNotice(CLAIM_SUCCESS);
        // The prompt opens only here, right after a connect (spec 2.1).
        setPrompt(response.data.askDestination || null);
      } catch (error) {
        if (!isCalendarOff(error)) showNotice(errorText(error), 'error');
        await loadView();
      } finally {
        setIsClaiming(false);
      }
    },
    [loadView, showNotice]
  );

  // The return from Google carries `#calendar_claim=<code>` or `#calendar_error=<code>`
  // (spec 2.2). The claim comes from sessionStorage first and from the fragment when the
  // first script did not move it. The fragment is removed before the claim is posted, so
  // a reload, a second run of this effect or a copied address never posts the code again.
  useEffect(() => {
    const storedClaim = takeStoredClaim();
    const fragment = new URLSearchParams(window.location.hash.slice(1));
    const claim = storedClaim || fragment.get('calendar_claim');
    const returnError = fragment.get('calendar_error');
    if (claim || returnError) {
      window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);
    }
    if (returnError) showNotice(returnErrorText(returnError), 'error');
    if (claim) claimConnection(claim);
    else loadView();
  }, [claimConnection, loadView, showNotice]);

  // The owner may change things in Google or in another tab, so the view is read again
  // whenever this window gets focus. Not while a save is open, so its answer is not undone.
  // With nothing left after a disconnect, the 404 page shows only on the next visit.
  useEventListener('focus', () => {
    const isBusy = isClaiming || isLoading || savingId || isDisconnecting || isSavingDestination;
    if (!isOff && !isBusy && !(view && isEmptyView(view))) loadView();
  });

  // Back from Google's consent page, the browser can restore this page as it was left,
  // with the buttons still waiting.
  useEventListener('pageshow', (event) => {
    if (event.persisted) setIsStarting(false);
  });

  // Connect, Reconnect and Grant permission all move this tab to Google's consent page.
  const startGoogle = async (purpose, connection) => {
    setIsStarting(true);
    try {
      const body = connection ? { purpose, connection: connection.id } : { purpose };
      const response = await axios.post('/calendar/google/start', body);
      window.location.assign(response.data.url);
    } catch (error) {
      showRefusal(error);
      setIsStarting(false);
    }
  };

  const setTicked = (connectionId, calendarId, ticked) =>
    setView((current) => ({
      ...current,
      connections: current.connections.map((connection) =>
        connection.id !== connectionId
          ? connection
          : {
              ...connection,
              calendars: connection.calendars.map((calendar) =>
                calendar.id === calendarId ? { ...calendar, ticked } : calendar
              ),
            }
      ),
    }));

  // A tick saves at once with the full ticked list of that connection. A refusal puts
  // the box back and shows the reason.
  const toggleCalendar = async (connection, calendar) => {
    const ticked = !calendar.ticked;
    const tickedIds = connection.calendars
      .filter((candidate) => (candidate.id === calendar.id ? ticked : candidate.ticked))
      .map((candidate) => candidate.id);
    setTicked(connection.id, calendar.id, ticked);
    writeCount.current += 1;
    setSavingId(connection.id);
    try {
      const response = await axios.put(`/calendar/connections/${encodeURIComponent(connection.id)}`, {
        ticked: tickedIds,
      });
      setView(response.data);
    } catch (error) {
      setTicked(connection.id, calendar.id, !ticked);
      showRefusal(error);
    } finally {
      setSavingId(null);
    }
  };

  // "Add bookings to" and the prompt's Yes. The body carries the connection and the calendar
  // id only, never the name (R2-S-020). Answers true when the server took the change.
  const saveDestination = async (destination) => {
    writeCount.current += 1;
    setIsSavingDestination(true);
    try {
      const body = destination ? { connection: destination.connection, calendarId: destination.calendarId } : null;
      const response = await axios.put('/calendar/settings', { destination: body });
      setView(response.data);
      return true;
    } catch (error) {
      showRefusal(error);
      // These mean the page shows an old view: writes went off, the calendar changed, or the
      // server paused.
      const isStale = ['calendar_writes_off', 'calendar_destination_not_owned', 'calendar_unavailable'];
      if (isStale.includes(error?.error)) loadView();
      return false;
    } finally {
      setIsSavingDestination(false);
    }
  };

  // A dialog stays clickable while it fades out, so a late click finds nothing to send.
  const acceptPrompt = async () => {
    if (!prompt) return;
    await saveDestination(prompt);
    setPrompt(null);
  };

  const chooseAnotherCalendar = () => {
    setPrompt(null);
    setIsPickerOpen(true);
  };

  // The DELETE answer is the view read again after the delete (spec 3.8 and 9.4).
  const disconnect = async () => {
    if (!toDisconnect) return;
    const { accountEmail } = toDisconnect;
    writeCount.current += 1;
    setIsDisconnecting(true);
    try {
      const response = await axios.delete(`/calendar/connections/${encodeURIComponent(toDisconnect.id)}`);
      setView(response.data);
      showNotice(`${accountEmail} is disconnected.`, 'success', isEmptyView(response.data));
    } catch (error) {
      showRefusal(error);
    } finally {
      setIsDisconnecting(false);
      setToDisconnect(null);
    }
  };

  if (isOff) return <Navigate to="/404" replace />;

  const mode = view?.mode || 'on';
  const connections = view?.connections || [];
  const isOn = mode === 'on';
  const nothingTicked = connections.every((connection) =>
    (connection.calendars || []).every((calendar) => !calendar.ticked)
  );
  // A tick save and a destination save both answer the whole view, so only one runs at a time.
  const isSavingAny = savingId !== null || isSavingDestination;

  let content = null;
  if (isClaiming) {
    content = (
      <Stack direction="row" spacing={2} alignItems="center">
        <CircularProgress size={24} />
        <Typography variant="body2">Finishing the connection</Typography>
      </Stack>
    );
  } else if (isLoading) {
    content = <Skeleton variant="rectangular" height={160} sx={{ borderRadius: 2 }} />;
  } else if (view) {
    content = (
      <>
        {mode === 'disconnect_only' && connections.length > 0 && (
          <Alert severity="info" sx={{ mb: 3 }}>
            {DISCONNECT_ONLY_NOTE}
          </Alert>
        )}
        {mode === 'paused' && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            {pausedNote(oldestCheckedAt(connections), zone)}
          </Alert>
        )}

        {isOn && !connections.length && (
          <Card sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6">Connect Google Calendar</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1, mb: 3 }}>
              {INTRO}
            </Typography>
            <LoadingButton
              variant="contained"
              loading={isStarting}
              startIcon={<Iconify icon="eva:google-fill" />}
              onClick={() => startGoogle('connect')}
            >
              Connect Google
            </LoadingButton>
          </Card>
        )}

        {connections.map((connection) => (
          <ConnectionCard
            key={connection.id}
            connection={connection}
            zone={zone}
            mode={mode}
            writesAllowed={Boolean(view.writesAllowed)}
            isSaving={isSavingAny}
            isStarting={isStarting}
            onToggle={(calendar) => toggleCalendar(connection, calendar)}
            onReconnect={() => startGoogle('reconnect', connection)}
            onGrant={() => startGoogle('grant', connection)}
            onDisconnect={() => setToDisconnect(connection)}
          />
        ))}

        {isOn && connections.length > 0 && nothingTicked && (
          <Alert severity="info" sx={{ mb: 3 }}>
            {NOTHING_TICKED}
          </Alert>
        )}
        {isOn && connections.length > 0 && (
          <LoadingButton
            variant="outlined"
            loading={isStarting}
            startIcon={<Iconify icon="eva:google-fill" />}
            onClick={() => startGoogle('connect')}
          >
            Connect another Google account
          </LoadingButton>
        )}
        {isOn && connections.length > 0 && (
          <DestinationCard
            connections={connections}
            settings={view.settings}
            writesAllowed={Boolean(view.writesAllowed)}
            isSaving={isSavingAny}
            isPickerOpen={isPickerOpen}
            onPickerOpenChange={setIsPickerOpen}
            onSave={saveDestination}
          />
        )}
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Google Calendar | {localStorage.getItem('companyName') ?? 'FlowBookings'}</title>
      </Helmet>

      {/* amp-block keeps Amplitude's session replay and autocapture off this page (R1-L3-002). */}
      <Container maxWidth="xl" className="amp-block">
        <Typography variant="h4" sx={{ mb: 3 }}>
          Google Calendar
        </Typography>

        {notice && (
          <Alert
            severity={notice.severity}
            color={notice.severity === 'success' ? 'info' : notice.severity}
            sx={{ mb: 3 }}
            onClose={() => setNotice(null)}
          >
            {notice.message}
          </Alert>
        )}

        {content}
      </Container>

      <DisconnectDialog
        connection={toDisconnect}
        isDisconnecting={isDisconnecting}
        onCancel={() => setToDisconnect(null)}
        onConfirm={disconnect}
      />

      <DestinationPrompt
        destination={prompt}
        upcoming={view?.settings?.upcomingToAdd || 0}
        isSaving={isSavingDestination}
        onNotNow={() => setPrompt(null)}
        onChoose={chooseAnotherCalendar}
        onYes={acceptPrompt}
      />
    </>
  );
}
